const ARASAAC_API_BASE = 'https://api.arasaac.org/api';
const ARASAAC_IMAGE_BASE = 'https://static.arasaac.org/pictograms';

export interface ArasaacKeyword {
  keyword?: string;
  plural?: string;
  meaning?: string;
}

export interface ArasaacPictogram {
  _id: number;
  keywords?: ArasaacKeyword[];
  tags?: string[];
  categories?: string[];
  schematic?: boolean;
}

function isArasaacPictogram(value: unknown): value is ArasaacPictogram {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate._id === 'number' && Number.isFinite(candidate._id);
}

export async function searchArasaacPictograms(
  query: string,
  options?: {
    language?: string;
    limit?: number;
  }
): Promise<ArasaacPictogram[]> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [];

  const language = options?.language ?? 'en';
  const response = await fetch(
    `${ARASAAC_API_BASE}/pictograms/${language}/search/${encodeURIComponent(trimmedQuery)}`
  );

  if (!response.ok) {
    const responseBody = await response.text().catch(() => '');
    throw new Error(`ARASAAC request failed (${response.status}). ${responseBody.slice(0, 200)}`);
  }

  const payload: unknown = await response.json();
  if (!Array.isArray(payload)) return [];

  const limit = options?.limit ?? 30;
  return payload.filter(isArasaacPictogram).slice(0, limit);
}

export function getArasaacImageUrl(pictogram: ArasaacPictogram, size = 300): string {
  const safeSize = Number.isFinite(size) && size > 0 ? Math.floor(size) : 300;
  return `${ARASAAC_IMAGE_BASE}/${pictogram._id}/${pictogram._id}_${safeSize}.png`;
}

export function generateArasaacAltText(pictogram: ArasaacPictogram, searchTerm?: string): string {
  const firstKeyword = pictogram.keywords?.find(
    (entry) => typeof entry?.keyword === 'string' && entry.keyword.trim().length > 0
  )?.keyword;
  if (firstKeyword) return firstKeyword.trim();
  const fallback = searchTerm?.trim();
  return fallback ? `Pictogram for ${fallback}` : 'Pictogram from ARASAAC';
}

export function buildArasaacCredit(): string {
  return 'Pictogram via ARASAAC';
}
